import { CARRIER_COMPANY, COOKIE_NAMES, USER_ACCESS_LEVELS } from "shared";

import {
    AUTH_ERROR,
    CHANGE_USER_COMPANY_LEVEL,
    CHANGE_USER_CUSTOM_RESOURCE_LIMITS,
    REMOVE_LOADING,
    REMOVE_MESSAGE,
    RESET_AUTH_TYPE,
    SET_COOKIE_CONSENT,
    SET_LOADING,
    SET_USER,
    TOGGLE_2FA_ENABLE,
    TOGGLE_2FA_VERIFICATION
} from "./authActions";
import {
    createPermissions,
    getDefaultPermissionState,
    handle2FaEnableFlowSeen
} from "./functions";
import {
    AuthAction,
    AuthInitialState,
    Cookies,
    Toggle2FaEnableState,
    UserWithCompanyAttributes
} from "./types";

const AuthReducer = (
    state: AuthInitialState,
    action: AuthAction
): AuthInitialState => {
    switch (action.type) {
        case TOGGLE_2FA_VERIFICATION: {
            const { authType, value } = action.payload;

            return {
                ...state,
                message: null,
                authType,
                is2FaVerification: value,
                isLoading: false
            };
        }
        case TOGGLE_2FA_ENABLE: {
            const { is2FaSetupRequired, user } = state as Toggle2FaEnableState;
            const { value: isEnabled, authType } = action.payload;

            return {
                ...state,
                user: {
                    ...user,
                    two_factor_auth_enabled: isEnabled,
                    two_factor_auth_type: authType
                },
                authType,
                is2FaEnableFlowSeen: handle2FaEnableFlowSeen(
                    is2FaSetupRequired,
                    isEnabled
                )
            };
        }
        case RESET_AUTH_TYPE: {
            const { user } = state;

            return {
                ...state,
                user: user
                    ? {
                          ...user,
                          two_factor_auth_type: null
                      }
                    : null,
                authType: null
            };
        }
        case SET_USER: {
            const {
                user,
                companyLevelSettings,
                permissionList,
                accessGroups,
                hasSso,
                twoFactorSetupRequired
            } = action.payload;

            const { access_level, company_id, two_factor_auth_enabled } = user;

            const permissions = createPermissions(
                access_level === USER_ACCESS_LEVELS.User ? permissionList : null
            );

            return {
                ...state,
                user,
                permissions,
                accessGroups,
                hasSso,
                allCompanyLevelSettings: companyLevelSettings,
                message: null,
                authType: user.two_factor_auth_type,
                is2FaSetupRequired: twoFactorSetupRequired,
                is2FaEnableFlowSeen: handle2FaEnableFlowSeen(
                    twoFactorSetupRequired,
                    two_factor_auth_enabled
                ),
                is2FaVerification: false,
                isAuthenticated: true,
                isSupportUser: access_level >= USER_ACCESS_LEVELS.Support,
                isAdminUser: access_level >= USER_ACCESS_LEVELS.Admin,
                isAdminManagerUser:
                    access_level >= USER_ACCESS_LEVELS.AdminManager,
                isInternalIntegrationUser:
                    access_level >= USER_ACCESS_LEVELS.InternalIntegration,
                isSystemUser: access_level >= USER_ACCESS_LEVELS.SystemUser,
                isSuperUser: access_level >= USER_ACCESS_LEVELS.SuperUser,
                isCarrierUser: company_id === CARRIER_COMPANY,
                isLoading: false,
                isAuthLoading: false
            };
        }
        case AUTH_ERROR: {
            const message = action.payload?.message;
            const hasSso = action.payload?.hasSso;

            return {
                ...state,
                user: null,
                permissions: getDefaultPermissionState(),
                hasSso: hasSso ?? state.hasSso,
                allCompanyLevelSettings: null,
                message: message || null,
                authType: null,
                is2FaSetupRequired: true,
                is2FaVerification: false,
                isAuthenticated: false,
                isSupportUser: false,
                isAdminUser: false,
                isAdminManagerUser: false,
                isInternalIntegrationUser: false,
                isSystemUser: false,
                isSuperUser: false,
                isCarrierUser: false,
                isLoading: false,
                isAuthLoading: false
            };
        }
        case REMOVE_MESSAGE: {
            return {
                ...state,
                message: null
            };
        }
        case CHANGE_USER_COMPANY_LEVEL: {
            const { user } = state as { user: UserWithCompanyAttributes };

            return {
                ...state,
                user: {
                    ...user,
                    company_level: action.payload
                }
            };
        }
        case CHANGE_USER_CUSTOM_RESOURCE_LIMITS: {
            const { user } = state as { user: UserWithCompanyAttributes };

            return {
                ...state,
                user: {
                    ...user,
                    customResourceLimitSettings: action.payload
                }
            };
        }
        case SET_LOADING: {
            return {
                ...state,
                isLoading: true
            };
        }
        case REMOVE_LOADING: {
            return {
                ...state,
                isLoading: false
            };
        }
        case SET_COOKIE_CONSENT: {
            const yearFromNowToExpire = new Date(
                new Date().setFullYear(new Date().getFullYear() + 1)
            );

            const { cookieName, cookieValue } = action.payload;

            const allCookies = { ...state.cookieConsent };

            const allCookieNames = Object.keys(allCookies) as COOKIE_NAMES[];

            const setCookies = () => {
                if (cookieName === COOKIE_NAMES.All) {
                    const allCookiesToSet = {} as Cookies;

                    allCookieNames.forEach(nameOfCookie => {
                        document.cookie = `${nameOfCookie}=${cookieValue}; expires=${yearFromNowToExpire}`;

                        allCookiesToSet[nameOfCookie as keyof Cookies] =
                            cookieValue.toString();
                    });

                    return allCookiesToSet;
                } else {
                    document.cookie = `${cookieName}=${cookieValue}; expires=${yearFromNowToExpire}`;
                }
            };

            return {
                ...state,
                cookieConsent: {
                    ...state.cookieConsent,
                    ...setCookies()
                }
            };
        }
        default:
            return state;
    }
};

export default AuthReducer;
